// Phase RV-4a — useRolloverNotifications hook.
//
// Returns the authenticated user's rollover_notifications rows plus a
// markRead() mutator. Realtime channel name uses useId() (per-mount,
// not random) to match the JB-arc cleanup pattern.

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export type RolloverNotificationKind =
  | "skip_cycle"
  | "drift_detected"
  | "cycle_published"
  | "chain_advanced"
  | "chain_busted";

export interface RolloverNotificationRow {
  notification_id: string;
  user_id: string;
  chain_id: string | null;
  kind: RolloverNotificationKind;
  payload: Record<string, unknown> | null;
  slip_id: string | null;
  read_at: string | null;
  created_at: string;
}

export interface UseRolloverNotificationsState {
  notifications: RolloverNotificationRow[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markRead: (notification_id: string) => Promise<void>;
  refetch: () => Promise<void>;
}

const VALID_KINDS: ReadonlySet<RolloverNotificationKind> = new Set([
  "skip_cycle",
  "drift_detected",
  "cycle_published",
  "chain_advanced",
  "chain_busted",
]);

function isValidKind(k: unknown): k is RolloverNotificationKind {
  return typeof k === "string" && VALID_KINDS.has(k as RolloverNotificationKind);
}

function normalise(row: Record<string, unknown>): RolloverNotificationRow | null {
  if (typeof row.notification_id !== "string") return null;
  if (typeof row.user_id !== "string") return null;
  if (!isValidKind(row.kind)) return null;
  if (typeof row.created_at !== "string") return null;
  return {
    notification_id: row.notification_id,
    user_id: row.user_id,
    chain_id: typeof row.chain_id === "string" ? row.chain_id : null,
    kind: row.kind,
    payload:
      row.payload && typeof row.payload === "object"
        ? (row.payload as Record<string, unknown>)
        : null,
    slip_id: typeof row.slip_id === "string" ? row.slip_id : null,
    read_at: typeof row.read_at === "string" ? row.read_at : null,
    created_at: row.created_at,
  };
}

export function useRolloverNotifications(): UseRolloverNotificationsState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const userId = user?.id ?? null;
  const instanceId = useId();
  const [notifications, setNotifications] = useState<RolloverNotificationRow[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!userId) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    const res = await sb
      .from("rollover_notifications")
      .select(
        "notification_id, user_id, chain_id, kind, payload, slip_id, read_at, created_at",
      )
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (res.error) {
      setError(res.error.message ?? "Failed to load notifications");
      setNotifications([]);
      setLoading(false);
      return;
    }
    const rows = Array.isArray(res.data)
      ? (res.data as Array<Record<string, unknown>>)
      : [];
    const normalised = rows
      .map(normalise)
      .filter((r): r is RolloverNotificationRow => r !== null);
    setNotifications(normalised);
    setLoading(false);
  }, [supabase, userId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  // Realtime: own-uid filter with a per-mount useId() channel name.
  useEffect(() => {
    if (!userId) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    const channel: RealtimeChannel = sb
      .channel(`rollover_notifications:${userId}:${instanceId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "rollover_notifications",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          void refetch();
        },
      )
      .subscribe();
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any).removeChannel(channel);
    };
  }, [supabase, userId, refetch, instanceId]);

  const markRead = useCallback(
    async (notification_id: string): Promise<void> => {
      if (!userId) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = supabase as any;
      const nowIso = new Date().toISOString();
      // Optimistic
      setNotifications((cur) =>
        cur.map((n) =>
          n.notification_id === notification_id ? { ...n, read_at: nowIso } : n,
        ),
      );
      const res = await sb
        .from("rollover_notifications")
        .update({ read_at: nowIso })
        .eq("notification_id", notification_id)
        .eq("user_id", userId);
      if (res.error) {
        setError(res.error.message ?? "Failed to mark notification as read");
        // Revert optimistic change by re-fetching
        await refetch();
      }
    },
    [supabase, userId, refetch],
  );

  const unreadCount = useMemo(
    () => notifications.filter((n) => n.read_at === null).length,
    [notifications],
  );

  return useMemo<UseRolloverNotificationsState>(
    () => ({
      notifications,
      unreadCount,
      loading,
      error,
      markRead,
      refetch,
    }),
    [notifications, unreadCount, loading, error, markRead, refetch],
  );
}
